/** The platform's subject types, in the spellings they travel under.
 *
 * `Person` / `Company` / `Vessel` are the backend enum labels (what a
 * subject carries on the wire); `person` / `company` / `vessel` is the
 * `applicable_subject_types` vocabulary of scope components and docx
 * bindings, where `individual` is an accepted alias of `person`. A vessel is
 * identified by its IMO number (`additional_info.imo_number`), never by a
 * registration number, and its jurisdiction is its flag state.
 */

export const SUBJECT_TYPE_LABELS = ['Person', 'Company', 'Vessel'] as const;

export type SubjectTypeLabel = (typeof SUBJECT_TYPE_LABELS)[number];

/** Select data for every form that picks a subject type. */
export const SUBJECT_TYPE_OPTIONS = SUBJECT_TYPE_LABELS.map((label) => ({ value: label, label }));

/** The `applicable_subject_types` vocabulary, lower-case. */
export const APPLICABLE_SUBJECT_TYPES = ['person', 'company', 'vessel'] as const;

export type ApplicableSubjectType = (typeof APPLICABLE_SUBJECT_TYPES)[number];

export const APPLICABLE_SUBJECT_TYPE_OPTIONS: { value: ApplicableSubjectType; label: string }[] = [
  { value: 'person', label: 'Person' },
  { value: 'company', label: 'Company' },
  { value: 'vessel', label: 'Vessel' },
];

/** A stored applicable type as the vocabulary spells it: `individual`
 * (the older spelling) reads as `person`; casing is ignored. */
export function normaliseApplicableSubjectType(value: string): string {
  const lower = value.trim().toLowerCase();
  return lower === 'individual' ? 'person' : lower;
}

/** Whether a subject-type value, in any spelling, names a vessel. */
export function isVesselSubjectType(value: string | null | undefined): boolean {
  const lower = (value ?? '').trim().toLowerCase();
  return lower === 'vessel' || lower === 'ship';
}

const SUBJECT_TYPE_BADGE_COLORS: Record<string, string> = {
  person: 'blue',
  company: 'grape',
  vessel: 'teal',
};

/** The badge colour of a subject type in any spelling; grey when unknown. */
export function subjectTypeBadgeColor(value: string | null | undefined): string {
  return SUBJECT_TYPE_BADGE_COLORS[normaliseApplicableSubjectType(value ?? '')] ?? 'gray';
}

/** The label of a subject's jurisdiction field: a vessel's is its flag. */
export function jurisdictionFieldLabel(
  subjectType: string | null | undefined,
  plural = true
): string {
  if (isVesselSubjectType(subjectType)) {
    return 'Flag';
  }
  return plural ? 'Jurisdictions' : 'Jurisdiction';
}
